import { array, date, number, object, string } from 'yup';

import I18n from '@utils/i18n.utils';

export enum FieldValidationType {
  REQUIRED_STRING = 'REQUIRED_STRING',
  REQUIRED_STRING_SHORT = 'REQUIRED_STRING_SHORT',
  REQUIRED_STRING_LONG = 'REQUIRED_STRING_LONG',
  STRING_LONG = 'STRING_LONG',
  REQUIRED_NUMBER = 'REQUIRED_NUMBER',
  REQUIRED_PHONE_NUMBER = 'REQUIRED_PHONE_NUMBER',
  REQUIRED_PHONE_NUMBER_WITH_CODE = 'REQUIRED_PHONE_NUMBER_WITH_CODE',
  REQUIRED_EMAIL = 'REQUIRED_EMAIL',
  EMAIL = 'EMAIL',
  REQUIRED_ARRAY = 'REQUIRED_ARRAY',
  REQUIRED_SELECT_ITEM = 'REQUIRED_SELECT_ITEM',
  REQUIRED_TIME = 'REQUIRED_TIME',
  REQUIRED_DATE = 'REQUIRED_DATE',
  REQUIRED_NUMBER_MIN = 'REQUIRED_NUMBER_MIN',
  REQUIRED_NUMBER_MAX = 'REQUIRED_NUMBER_MAX',
  REQUIRED_NUMBER_MIN_MAX = 'REQUIRED_NUMBER_MIN_MAX',
  DATE = 'DATE',
  REQUIRED_QR = 'REQUIRED_QR',
}

const phoneRegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;
const qrCodeRegExp = /SPC\n0200\n1\n(CH|LI)(\d|[A-Z]){19}\n([SK])\n(.*\n)*([1-9]\d*([.,])\d*|0?([.,])\d*[1-9]\d*|[1-9]\d*)\nCHF\n([SK])\n.*\n.*\n.*\n.*\n.*\n.*\nQRR\n\d{27}\n.*\nEPD(\n)*(\n)*/;

const phoneRegExpWithCode = /^[1-9]\d{5,9}$/;

const fieldsValidation = (parameters?: {min?: number; max?: number}) => ({
  [FieldValidationType.REQUIRED_STRING]: string().nullable().required(I18n.t('errors.fields.required')),
  [FieldValidationType.REQUIRED_EMAIL]: string()
    .nullable()
    .required(I18n.t('errors.fields.required'))
    .email(I18n.t('errors.fields.email')),
  [FieldValidationType.EMAIL]: string()
    .nullable()
    .email(I18n.t('errors.fields.email')),
  [FieldValidationType.REQUIRED_ARRAY]: array()
    .nullable()
    .required(I18n.t('errors.fields.required'))
    .min(1, I18n.t('errors.fields.required')),
  [FieldValidationType.REQUIRED_NUMBER]: number().nullable().required(I18n.t('errors.fields.required')),
  [FieldValidationType.REQUIRED_NUMBER_MIN]: number().min(parameters?.min || 0).nullable().required(I18n.t('errors.fields.required')),
  [FieldValidationType.REQUIRED_NUMBER_MAX]: number().max(parameters?.max || 999).nullable().required(I18n.t('errors.fields.required')),
  [FieldValidationType.REQUIRED_NUMBER_MIN_MAX]: number().min(parameters?.min || 0).max(parameters?.max || 999).nullable().required(I18n.t('errors.fields.required')),
  [FieldValidationType.REQUIRED_SELECT_ITEM]: object().nullable().required(I18n.t('errors.fields.required')),
  [FieldValidationType.REQUIRED_TIME]: date()
    .nullable()
    .required(I18n.t('errors.fields.required'))
    .typeError(I18n.t('errors.fields.time')),
  [FieldValidationType.REQUIRED_PHONE_NUMBER]: string()
    .nullable()
    .matches(phoneRegExp, I18n.t('errors.fields.phoneNumberWrong'))
    .min(10, I18n.t('errors.fields.tooShort'))
    .max(10, I18n.t('errors.fields.tooLong'))
    .required(I18n.t('errors.fields.required')),
    [FieldValidationType.REQUIRED_PHONE_NUMBER_WITH_CODE]: string()
    .nullable()
    .matches(phoneRegExpWithCode, I18n.t('errors.fields.phoneNumberWrong'))
    .min(5, I18n.t('errors.fields.tooShort'))
    .max(9, I18n.t('errors.fields.tooLong'))
    .required(I18n.t('errors.fields.required')),
  [FieldValidationType.REQUIRED_STRING_SHORT]: string()
    .nullable()
    .min(0, I18n.t('errors.fields.tooShort'))
    .max(128, I18n.t('errors.fields.tooLong'))
    .required(I18n.t('errors.fields.required')),
  [FieldValidationType.REQUIRED_STRING_LONG]: string()
    .nullable()
    .min(0, I18n.t('errors.fields.tooShort'))
    .max(1024, I18n.t('errors.fields.tooLong'))
    .required(I18n.t('errors.fields.required')),
  [FieldValidationType.STRING_LONG]: string()
    .nullable()
    .max(1024, I18n.t('errors.fields.tooLong')),
  [FieldValidationType.REQUIRED_DATE]: date()
    .nullable()
    .required(I18n.t('errors.fields.required'))
    .typeError(I18n.t('errors.fields.time')),
  [FieldValidationType.DATE]: date().nullable().typeError(I18n.t('errors.fields.time')),
  [FieldValidationType.REQUIRED_QR]: string().nullable().required(I18n.t('errors.fields.required')).matches(qrCodeRegExp, I18n.t('errors.fields.qrCodeWrong')),

});

export const getFieldError = (value: any, validationType: FieldValidationType, parameters?: {min?: number; max?: number}) => {
  try {
    fieldsValidation(parameters)[validationType].validateSync(value);
  } catch (err: any) {
    return err.message;
  }
};

// eslint-disable-next-line @typescript-eslint/ban-types
export const checkIfErrors = (errors: Object) =>
  Object.values(errors).some((err) =>
    typeof err === 'string' || typeof err === 'undefined' ? !!err : checkIfErrors(err)
  );
